import {
  BreakEndDocument,
  BreakEndMutationVariables,
  BreakStartDocument,
  BreakStartMutationVariables,
  BulkCreateAttendanceShiftDocument,
  BulkCreateAttendanceShiftMutationVariables,
  BulkDestroyAttendanceShiftDocument,
  BulkDestroyAttendanceShiftMutationVariables,
  ClockInDocument,
  ClockInMutationVariables,
  ClockOutDocument,
  ClockOutMutationVariables,
  CreateAttendanceShiftDocument,
  CreateAttendanceShiftMutationVariables,
  DeleteAttendanceShiftDocument,
  DeleteAttendanceShiftMutationVariables,
  ForgotClockOutDocument,
  ForgotClockOutMutationVariables,
  TimeTrackingClockInPageDocument,
  TimesheetPageShiftFragmentDoc,
  UpdateAttendanceShiftDocument,
  UpdateAttendanceShiftMutationVariables,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'

export const useShiftActions = () => {
  const [createAttendanceShift, { loading: loadingCreate }] = useMutation(
    CreateAttendanceShiftDocument,
    {
      extractorFn: (data) => data.attendanceMutations.createAttendanceShift,
      update: (cache, { data }) => {
        const shift = data?.attendanceMutations.createAttendanceShift.shift
        if (!shift) return

        cache.modify({
          id: `AttendanceEmployee:${shift?.employeeId}`,
          fields: {
            attendanceShiftsConnection(existingConnection) {
              const newShiftRef = cache.writeFragment({
                data: shift,
                fragment: TimesheetPageShiftFragmentDoc,
                fragmentName: 'TimesheetPageShift',
              })

              return {
                ...existingConnection,
                nodes: [...existingConnection.nodes, newShiftRef],
                totalCount: existingConnection.totalCount + 1,
              }
            },
          },
        })
      },
    }
  )

  const [bulkCreateAttendanceShift, { loading: loadingBulkCreate }] = useMutation(
    BulkCreateAttendanceShiftDocument,
    {
      extractorFn: (data) => data.attendanceMutations.bulkCreateAttendanceShift,
    }
  )

  const [updateAttendanceShift, { loading: loadingUpdate }] = useMutation(
    UpdateAttendanceShiftDocument,
    {
      extractorFn: (data) => data.attendanceMutations.updateAttendanceShift,
    }
  )

  const [deleteAttendanceShift, { loading: loadingDelete }] = useMutation(
    DeleteAttendanceShiftDocument,
    {
      extractorFn: (data) => data.attendanceMutations.deleteAttendanceShift,
    }
  )

  const [bulkDestroyAttendanceShift, { loading: loadingBulkDestroy }] = useMutation(
    BulkDestroyAttendanceShiftDocument,
    {
      extractorFn: (data) => data.attendanceMutations.bulkDestroyAttendanceShift,
    }
  )

  const createShift = (variables: CreateAttendanceShiftMutationVariables) => {
    return createAttendanceShift({
      variables,
    })
  }

  const bulkCreateShift = (variables: BulkCreateAttendanceShiftMutationVariables) => {
    return bulkCreateAttendanceShift({
      variables,
      refetchQueries: [TimeTrackingClockInPageDocument],
    })
  }

  const updateShift = (variables: UpdateAttendanceShiftMutationVariables) => {
    return updateAttendanceShift({
      variables,
    })
  }

  const deleteShift = (variables: DeleteAttendanceShiftMutationVariables) => {
    return deleteAttendanceShift({
      variables,
      update(cache, { data }) {
        const shift = data?.attendanceMutations.deleteAttendanceShift?.shift

        cache.evict({
          id: cache.identify({ id: shift?.id, __typename: 'AttendanceShift' }),
        })
      },
    })
  }

  const bulkDestroyShift = (variables: BulkDestroyAttendanceShiftMutationVariables) => {
    return bulkDestroyAttendanceShift({
      variables,
      refetchQueries: [TimeTrackingClockInPageDocument],
    })
  }

  const [clockIn, { loading: loadingClockIn }] = useMutation(ClockInDocument, {
    extractorFn: (data) => data.attendanceMutations.clockInAttendanceShift,
    update: (cache, { data }) => {
      const shift = data?.attendanceMutations.clockInAttendanceShift.shift
      if (!shift) return

      cache.modify({
        id: `AttendanceEmployee:${shift?.employeeId}`,
        fields: {
          attendanceShiftsConnection(existingConnection) {
            const newShiftRef = cache.writeFragment({
              data: { ...shift, __typename: 'AttendanceShift' },
              fragment: TimesheetPageShiftFragmentDoc,
              fragmentName: 'TimesheetPageShift',
            })

            return {
              ...existingConnection,
              nodes: [...existingConnection.nodes, newShiftRef],
              totalCount: existingConnection.totalCount + 1,
            }
          },
        },
      })
    },
  })

  const [clockOut, { loading: loadingClockOut }] = useMutation(ClockOutDocument, {
    extractorFn: (data) => data.attendanceMutations.clockOutAttendanceShift,
    update: (cache, { data }) => {
      const shift = data?.attendanceMutations.clockOutAttendanceShift.shift

      if (!shift) return

      cache.modify({
        id: `AttendanceEmployee:${shift?.employeeId}`,
        fields: {
          attendanceShiftsConnection(existingConnection, { readField }) {
            const existingIds = new Set(
              existingConnection.nodes.map((item: { _ref: string }) => readField('id', item))
            )

            const shifts = shift.employee?.attendanceShiftsConnection?.nodes || []
            const newItems = shifts.filter((item) => !existingIds.has(item.id))

            const newShifts = newItems.map((item) =>
              cache.writeFragment({
                data: { ...item, __typename: 'AttendanceShift' },
                fragment: TimesheetPageShiftFragmentDoc,
                fragmentName: 'TimesheetPageShift',
              })
            )

            return {
              ...existingConnection,
              nodes: [...existingConnection.nodes, ...newShifts],
              totalCount: existingConnection.totalCount + newShifts.length,
            }
          },
        },
      })
    },
  })

  const [forgotClockOut, { loading: loadingForgotClockOut }] = useMutation(ForgotClockOutDocument, {
    extractorFn: (data) => data.attendanceMutations.forgotClockOutAttendanceShift,
  })

  const [breakStart, { loading: loadingBreakStart }] = useMutation(BreakStartDocument, {
    extractorFn: (data) => data.attendanceMutations.breakStartAttendanceShift,
    update: (cache, { data }) => {
      const shift = data?.attendanceMutations.breakStartAttendanceShift.shift
      if (!shift) return

      cache.modify({
        id: `AttendanceEmployee:${shift?.employeeId}`,
        fields: {
          attendanceShiftsConnection(existingConnection, { readField }) {
            const existingIds = new Set(
              existingConnection.nodes.map((item: { _ref: string }) => readField('id', item))
            )

            const shifts = shift.employee?.attendanceShiftsConnection?.nodes || []
            const newItems = shifts.filter((item) => !existingIds.has(item.id))

            const newShifts = newItems.map((item) =>
              cache.writeFragment({
                data: { ...item, __typename: 'AttendanceShift' },
                fragment: TimesheetPageShiftFragmentDoc,
                fragmentName: 'TimesheetPageShift',
              })
            )

            return {
              ...existingConnection,
              nodes: [...existingConnection.nodes, ...newShifts],
              totalCount: existingConnection.totalCount + newShifts.length,
            }
          },
        },
      })
    },
  })

  const [breakEnd, { loading: loadingBreakEnd }] = useMutation(BreakEndDocument, {
    extractorFn: (data) => data.attendanceMutations.breakEndAttendanceShift,
    update: (cache, { data }) => {
      const shift = data?.attendanceMutations.breakEndAttendanceShift.shift

      if (!shift) return

      cache.modify({
        id: `AttendanceEmployee:${shift?.employeeId}`,
        fields: {
          attendanceShiftsConnection(existingConnection, { readField }) {
            const existingIds = new Set(
              existingConnection.nodes.map((item: { _ref: string }) => readField('id', item))
            )

            const shifts = shift.employee?.attendanceShiftsConnection?.nodes || []
            const newItems = shifts.filter((item) => !existingIds.has(item.id))

            const newShifts = newItems.map((item) =>
              cache.writeFragment({
                data: { ...item, __typename: 'AttendanceShift' },
                fragment: TimesheetPageShiftFragmentDoc,
                fragmentName: 'TimesheetPageShift',
              })
            )

            return {
              ...existingConnection,
              nodes: [...existingConnection.nodes, ...newShifts],
              totalCount: existingConnection.totalCount + newShifts.length,
            }
          },
        },
      })
    },
  })

  const shiftClockIn = (variables: ClockInMutationVariables) => {
    return clockIn({
      variables,
    })
  }

  const shiftClockOut = (variables: ClockOutMutationVariables) => {
    return clockOut({
      variables,
    })
  }

  const shiftForgotClockOut = (variables: ForgotClockOutMutationVariables) => {
    return forgotClockOut({
      variables,
    })
  }

  const shiftBreakStart = (variables: BreakStartMutationVariables) => {
    return breakStart({
      variables,
    })
  }

  const shiftBreakEnd = (variables: BreakEndMutationVariables) => {
    return breakEnd({
      variables,
    })
  }

  return {
    shiftClockIn,
    shiftClockOut,
    shiftForgotClockOut,
    shiftBreakStart,
    shiftBreakEnd,
    loadingClockIn,
    loadingClockOut,
    loadingForgotClockOut,
    loadingBreakStart,
    loadingBreakEnd,
    loadingCreate,
    loadingBulkCreate,
    loadingUpdate,
    loadingDelete,
    loadingBulkDestroy,
    createShift,
    bulkCreateShift,
    updateShift,
    deleteShift,
    bulkDestroyShift,
  }
}
